import { StoreName } from "../../decorators/StoreNameDecorator";
import { LicenseService } from "../../licensing/LicenseService";
import API from "../../models/API";
import {
  BatchClassConfig,
  BatchClassConfigData,
} from "../../models/config/BatchClassConfig";
import { sortBatchClasses } from "../../models/SweetListSorter";
import { AbstractRemovableStore } from "../RemovableStore";
import { AbstractSaveableStore } from "../SaveableStore";
import { cleanBatchClassConfig } from "../util/BCCStoreUtils";

/**
 * Export methods for mass batch class operations
 */
export { BatchClassConfigStore, heliosify, unheliosify };

const DEFAULT_OCR_CONFIDENCE = 90;

@StoreName("BatchClassConfigStore")
class BatchClassConfigStoreClass<_ extends string, __ extends BatchClassConfig>
  extends AbstractSaveableStore<string, BatchClassConfig>
  implements AbstractRemovableStore<string, BatchClassConfig>
{
  private HELIOS_ENABLED = false;
  async _setup() {
    this.HELIOS_ENABLED =
      (await (await LicenseService.getLicenseDetails())?.helios) ?? false;
  }

  public getThenClearOthers(
    key: string,
  ): Promise<BatchClassConfig | undefined> {
    this.values = this.values.filter((x) => x.batchClassId === key);
    const bcc = this.getWithoutLookup(key);
    return bcc ? Promise.resolve(bcc) : BatchClassConfigStore.get(key);
  }

  /**
   * Override and add missing helios data before calling inherited set()
   * @param value
   * @param overwrite
   */
  set(value: BatchClassConfig, overwrite?: boolean): BatchClassConfig {
    return super.set(
      this.HELIOS_ENABLED ? heliosify(value) : unheliosify(value),
      overwrite,
    );
  }

  setMany(values: BatchClassConfig[], overwrite?: boolean): BatchClassConfig[] {
    return values.map((x) => this.set(x, overwrite));
  }

  removeAll(): BatchClassConfig[] {
    return this.values.splice(0, this.values.length);
  }
  remove(key: string): BatchClassConfig | undefined {
    // get value by key
    const value = this.getWithoutLookup(key);

    if (!value) return;

    return this.values.splice(this.values.indexOf(value), 1)[0];
  }
  removeByValue(value: BatchClassConfig): void {
    const index: number = this.values.indexOf(value);
    if (index == -1) return;
    this.values.splice(this.values.indexOf(value), 1);
  }

  storedObjectKeysEquals(a: string, b: string): boolean {
    return a === b;
  }

  /**
   * Send the API a delete request, remove the BatchClassConfig from this store if the request was successful
   */
  async delete(batchClassId: string, hard?: boolean): Promise<boolean> {
    const bccIndex = this.values.findIndex(
      (x) => x.batchClassId === batchClassId,
    );
    const exists = await this.bccExists(batchClassId);
    let result;
    if (exists) {
      const deleteParams: { hardDelete: undefined | string } = {
        hardDelete: undefined,
      };
      if (hard != undefined) {
        deleteParams.hardDelete = hard + "";
      } else {
        delete deleteParams["hardDelete"];
      }
      result = await API.delete(
        `/v0/config/${batchClassId}`,
        deleteParams as {},
      )
        .then((res) => res.ok)
        .catch((err) => {
          console.log(`Could not delete BatchClassConfig ${batchClassId}`, err);
          return false;
        });
    } else {
      result = true;
    }
    if (result && bccIndex > -1) {
      this.values.splice(bccIndex, 1);
    }
    return result;
  }

  async saveByValue(value: BatchClassConfig): Promise<boolean> {
    return this.saveByValueWithResp(value) != null;
  }
  /**
   * This method sends a POST request if the BCC doesn't already exist, and sends a PATCH request if it already does.
   * This will work and reject users that try updating BCCs they cannot READ :)
   * @param value
   * @returns
   */
  async saveByValueWithResp(
    value: BatchClassConfig,
  ): Promise<BatchClassConfig | null> {
    const clean = cleanBatchClassConfig(value)[0].toData();
    const exists = await this.bccExists(clean.batchClassId).catch((e) => {
      console.log(
        `Checking if batch class ${clean.batchClassId} exists failed, cannot save`,
      );
      return null;
    });
    if (exists == null) return null;
    let updatePromise;
    if (exists) {
      updatePromise = API.patch(
        `/v0/config/${clean.batchClassId}`,
        {
          // always overwrite batch clas
          overrideAccelerator: "true",
        },
        clean,
      );
    } else {
      updatePromise = API.post(`/v0/config`, null, clean);
    }
    // save BCC first
    return updatePromise
      .then((resp: Response) => {
        if (!resp.ok) {
          throw new Error();
        }
        return resp.json();
      })
      .then((resp: BatchClassConfigData) => new BatchClassConfig(resp))
      .catch((err) => {
        console.log("Could not save BCC", err);
        return null;
      });
  }

  /**
   * Fetch batch class by batchClassId
   */
  public lookup(batchClassId: string): Promise<BatchClassConfig | undefined> {
    return API.get(`/v0/config/${batchClassId}`)
      .then(async (value: Response) => {
        if (!value.ok) {
          return undefined;
        }
        const data: BatchClassConfigData =
          (await value.json()) as BatchClassConfigData;
        if (data) {
          return new BatchClassConfig(data);
        } else {
          return undefined;
        }
      })
      .catch(() => {
        return undefined;
      });
  }

  private async bccExists(batchClassId: string) {
    return API.get(`/v0/config/${batchClassId}`).then((r) => r.ok);
  }

  getAll(): Array<BatchClassConfig> {
    const returnValues = this.values;
    sortBatchClasses(returnValues);
    // SweetListSorter.sort(returnValues, (data) => data.batchClassId, 'BC', 16);
    return returnValues;
  }

  //@ts-expect-error
  _vamp(values) {
    //@ts-expect-error
    this.values = values.map((r) => new BatchClassConfig(r));
  }
  _unvamp() {
    return this.values;
  }
}

const BatchClassConfigStore = new BatchClassConfigStoreClass();

/**
 * Add default helios property to all fields if required. This is suitable for running on any Batch Class Config.
 * @param batchclassconfig
 * @returns
 */
function heliosify(batchclassconfig: BatchClassConfig): BatchClassConfig {
  // Always enable EText Normalization for Helios batch class configs
  batchclassconfig.normalizationType = "NATIVE";
  // Always enable Accelerator OCR Thresholds for Helios
  batchclassconfig.acceleratorOcrThresholds = true;

  if (!batchclassconfig.helios) {
    batchclassconfig.helios = {
      batchClassDescription: batchclassconfig.batchClassName,
      batchClassFields: [],
      priority: 1,
    };
  } else {
    if (!batchclassconfig.helios?.batchClassDescription)
      batchclassconfig.helios.batchClassDescription =
        batchclassconfig.batchClassName;
    if (!batchclassconfig.helios?.batchClassFields)
      batchclassconfig.helios.batchClassFields = [];
    if (batchclassconfig.helios?.priority == undefined)
      batchclassconfig.helios.priority = 1;
  }
  batchclassconfig.docTypes.forEach((dtc, dtcI) => {
    if (!dtc.helios) {
      dtc.helios = {
        canAssign: true,
        canChange: true,
        position: dtcI,
        autoCreateTemplate: false,
      };
    } else {
      if (dtc.helios.position == undefined) dtc.helios.position = dtcI;
      if (dtc.helios?.canAssign == undefined) dtc.helios.canAssign = true;
      if (dtc.helios?.canChange == undefined) dtc.helios.canChange = true;
      if (dtc.helios?.autoCreateTemplate == undefined)
        dtc.helios.autoCreateTemplate = false;
    }
  });
  batchclassconfig.docTypes
    .flatMap((x) => x.fields)
    .forEach((dlf, i) => {
      if (!dlf.helios) {
        dlf.helios = {
          fieldType: "TEXT",
          fieldValues: [],
          forceReview: false,
          hidden: false,
          position: dlf.position,
          readOnly: false,
          tooltip: "",
          startPatternCandidate: false,
          uniqueField: false,
          templateNamingField: false,
          autoCreateRule: false,
        };
      } else {
        if (!dlf.helios?.fieldType) dlf.helios.fieldType = "TEXT";
        if (!dlf.helios?.fieldValues) dlf.helios.fieldValues = [];
        if (dlf.helios?.forceReview == undefined)
          dlf.helios.forceReview = false;
        if (dlf.helios?.hidden == undefined) dlf.helios.hidden = false;
        if (dlf.helios?.position == undefined) dlf.helios.position = dlf.position;
        if (dlf.helios?.readOnly == undefined) dlf.helios.readOnly = false;
        if (!dlf.helios?.tooltip) dlf.helios.tooltip = "";
        if (dlf.helios?.startPatternCandidate == undefined)
          dlf.helios.startPatternCandidate = false;
        if (dlf.helios?.uniqueField == undefined)
          dlf.helios.uniqueField = false;
        if (dlf.helios?.templateNamingField == undefined)
          dlf.helios.templateNamingField = false;
        if (dlf.helios?.autoCreateRule == undefined)
          dlf.helios.autoCreateRule = false;
      }
    });

  batchclassconfig.docTypes
    .flatMap((x) => x.tables)
    .forEach((table, i) => {
      if (!table.helios) {
        table.helios = {
          hidden: false,
          position: table.position,
          description: table.name,
        };
      } else {
        if (table.helios?.position == undefined) table.helios.position = table.position;
        if (table.helios?.hidden == undefined) table.helios.hidden = false;
        if (!table.helios?.description) table.helios.description = table.name;
      }
    });

  batchclassconfig.docTypes
    .flatMap((x) => x.tables.flatMap((t) => t.columnNames))
    .forEach((column, i) => {
      if (!column.helios) {
        column.helios = {
          fieldType: "TEXT",
          fieldValues: [],
          forceReview: false,
          hidden: false,
          position: column.position,
          readOnly: false,
          tooltip: "",
        };
      } else {
        if (!column.helios?.fieldType) column.helios.fieldType = "TEXT";
        if (!column.helios?.fieldValues) column.helios.fieldValues = [];
        if (column.helios?.forceReview == undefined)
          column.helios.forceReview = false;
        if (column.helios?.hidden == undefined) column.helios.hidden = false;
        if (column.helios?.position == undefined) column.helios.position = column.position;
        if (column.helios?.readOnly == undefined)
          column.helios.readOnly = false;
        if (!column.helios?.tooltip) column.helios.tooltip = "";
      }
    });
  return batchclassconfig;
}

function unheliosify(batchclassconfig: BatchClassConfig): BatchClassConfig {
  batchclassconfig.helios = undefined;
  batchclassconfig.docTypes.forEach((x) => (x.helios = undefined));
  batchclassconfig.docTypes
    .flatMap((x) => x.fields)
    .forEach((x) => (x.helios = undefined));
  batchclassconfig.docTypes
    .flatMap((x) => x.tables)
    .forEach((x) => (x.helios = undefined));
  batchclassconfig.docTypes
    .flatMap((x) => x.tables)
    .flatMap((x) => x.columnNames)
    .forEach((x) => (x.helios = undefined));
  return batchclassconfig;
}
